import React, { useEffect, useState } from 'react';
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    FormGroup,
    Label,
    Input,
    Collapse,
    Badge,
} from 'reactstrap';
import './ExportModal.scss';

const ExportModal = ({
    isOpen,
    toggle,
    onExport,
    fields = [],
    subFields = {},
    defaultSelectedFields = [],
    entityName = 'Data',
}) => {
    const [selectedFields, setSelectedFields] = useState([]);
    const [openSubFields, setOpenSubFields] = useState(() => {
        const initial = {};
        Object.keys(subFields).forEach(key => {
            initial[key] = true;
        });
        return initial;
    });

    const toggleSubField = (key) => {
        setOpenSubFields(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const handleCheckboxChange = (field) => {
        setSelectedFields(prev =>
            prev.includes(field)
                ? prev.filter(item => item !== field)
                : [...prev, field]
        );
    };

    const handleExport = filetype => () => {
        onExport(selectedFields, filetype);
        toggle();
    };

    const handleSubFieldChange = (parentField, subField) => {
        const fullField = `${parentField}.${subField}`;
        handleCheckboxChange(fullField);
    };

    useEffect(() => {
        if (isOpen) {
            setSelectedFields(defaultSelectedFields);
        }
    }, [isOpen, defaultSelectedFields]);

    const getSelectedSubFieldCount = (parentField) => {
        return subFields[parentField]?.filter(subField =>
            selectedFields.includes(`${parentField}.${subField}`)
        ).length || 0;
    };

    const selectAll = () => {
        setSelectedFields([
            ...fields,
            ...Object.keys(subFields).flatMap(parentField =>
                subFields[parentField].map(subField => `${parentField}.${subField}`)
            ),
        ]);
    };

    const clearAll = () => {
        setSelectedFields([]);
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} scrollable className="export-modal">
            <ModalHeader toggle={toggle}>
                Export {entityName}
            </ModalHeader>
            <ModalBody>
                <div className="d-flex justify-content-between mb-3">
                    <Button
                        color="link"
                        className="px-0 text-decoration-none"
                        size="sm"
                        onClick={selectAll}
                    >
                        <span className="fas fa-check-square me-2" />
                        Select All
                    </Button>
                    <Button
                        color="link"
                        className="px-0 text-decoration-none"
                        size="sm"
                        onClick={clearAll}
                    >
                        <span className="fas fa-times-circle me-2" />
                        Clear All
                    </Button>
                </div>

                {fields.map(field => (
                    <FormGroup key={field} check>
                        <Label check>
                            <Input
                                type="checkbox"
                                value={field}
                                checked={selectedFields.includes(field)}
                                onChange={() => handleCheckboxChange(field)}
                            />
                            {field}
                        </Label>
                    </FormGroup>
                ))}

                {Object.keys(subFields).map(parentField => (
                    <div key={parentField} className="mt-2">
                        <Label check>
                            <Badge color="primary" pill className="me-2 text-black">
                                {getSelectedSubFieldCount(parentField)}
                            </Badge>
                            {parentField}
                        </Label>
                        <Button
                            color="link"
                            size="sm"
                            className="text-decoration-none"
                            onClick={() => toggleSubField(parentField)}
                        >
                            {openSubFields[parentField] ? 'Collapse' : 'Expand'}
                        </Button>
                        <Collapse isOpen={openSubFields[parentField]}>
                            {subFields[parentField].map(subField => {
                                const fullField = `${parentField}.${subField}`;
                                return (
                                    <FormGroup key={subField} check style={{ marginLeft: '20px' }}>
                                        <Label check>
                                            <Input
                                                type="checkbox"
                                                value={fullField}
                                                checked={selectedFields.includes(fullField)}
                                                onChange={() => handleSubFieldChange(parentField, subField)}
                                            />
                                            {subField}
                                        </Label>
                                    </FormGroup>
                                );
                            })}
                        </Collapse>
                    </div>
                ))}
            </ModalBody>
            <ModalFooter className="d-flex justify-content-between">
                <Button color="primary" outline onClick={toggle}>
                    <span className="fas fa-times me-2" />
                    Cancel
                </Button>
                <div className="d-flex gap-2">
                    <Button
                        color="primary"
                        onClick={handleExport('csv')}
                        disabled={selectedFields.length === 0}
                    >
                        <span className="fas fa-download me-2" />
                        Export CSV
                    </Button>
                    <Button
                        color="primary"
                        onClick={handleExport('xlsx')}
                        disabled={selectedFields.length === 0}
                    >
                        <span className="fas fa-file-excel me-2" />
                        Export Excel
                    </Button>
                </div>
            </ModalFooter>
        </Modal>
    );
};

export default ExportModal;
